import React, { CSSProperties, useContext, useRef } from 'react';
import GameContext from 'contexts/GameContext';
import { useFeatureFlag, useFeatureFlagExposure } from 'featureFlags';
import { useSngPayouts } from 'hooks/useSngPayouts';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTournamentTableExit } from 'hooks/useTournamentTableExit';
import { TableZ } from 'styles/zIndex';
import { currencyFormatter } from 'utils/functions';

import { firstPlacePrize, SitGoWaitingState, waitingHeadline } from './sitGoWaitingState';
import {
  CONNECTION_LOST_MESSAGE,
  STILL_CONFIRMING_MESSAGE,
  useSitGoUnregister
} from './useSitGoUnregister';
import { useSitGoPreStart } from './useSitGoWaiting';

import styles from './SitGoWaiting.module.scss';

// Its own layer on the table ladder, below every seat (TableZ.seat): a seat
// card that meets the panel paints over it. Inline so the ladder stays the one
// place the number lives.
const LAYER: CSSProperties = { zIndex: TableZ.sitGoWaitingPanel };

const WaitingPanel = ({ waiting }: { waiting: SitGoWaitingState }) => {
  const { setShowTournamentDetails } = useContext(GameContext);
  const gameID = useTableSelector((table) => table.game?.gameID);
  const exitTournamentTable = useTournamentTableExit();
  const leaveTable = () => exitTournamentTable();
  // Kept fresh (the hook's default), so a failed or throttled fetch is retried
  // every 30s and on focus/online instead of hiding the prize for the visit.
  const payouts = useSngPayouts(waiting.tournamentId, true);
  // A ready result is authoritative, including one where first place is now a
  // ticket (no cash prize). A failed refresh publishes no rows; only then, or
  // while loading, keep showing the last cash prize that loaded here.
  const lastPrizeRef = useRef<{ tournamentId: number; amount: number } | null>(null);
  const loadedPrize = firstPlacePrize(payouts.rows);
  if (payouts.status === 'ready') {
    lastPrizeRef.current =
      loadedPrize === undefined
        ? null
        : { tournamentId: waiting.tournamentId, amount: loadedPrize };
  }
  const firstPrize =
    payouts.status === 'ready'
      ? loadedPrize
      : lastPrizeRef.current?.tournamentId === waiting.tournamentId
      ? lastPrizeRef.current.amount
      : undefined;
  const { requestUnregister, pending, stillConfirming, connectionLost, failure } =
    useSitGoUnregister({
      tournamentId: waiting.tournamentId,
      gameID,
      onUnregistered: leaveTable
    });

  return (
    <div className={styles.layer} style={LAYER} data-testid='sit-go-waiting-layer'>
      <section className={styles.panel} aria-label='Sit & Go waiting to start'>
        <p className={styles.headline} aria-live='polite'>
          {waitingHeadline(waiting.openSeats)}
        </p>
        <p className={styles.subline}>Starts when the table is full</p>
        <dl className={styles.facts}>
          {waiting.buyIn !== undefined && (
            <div className={styles.fact}>
              <dt className={styles.factLabel}>Buy-in</dt>
              <dd className={styles.factValue}>{currencyFormatter(waiting.buyIn)}</dd>
            </div>
          )}
          {firstPrize !== undefined && (
            <div className={styles.fact}>
              <dt className={styles.factLabel}>1st prize</dt>
              <dd className={styles.factValue}>{currencyFormatter(firstPrize)}</dd>
            </div>
          )}
        </dl>
        {stillConfirming && (
          <p className={styles.notice} role='status'>
            {STILL_CONFIRMING_MESSAGE}
          </p>
        )}
        {connectionLost && !pending && (
          <p className={styles.failure} role='alert'>
            {CONNECTION_LOST_MESSAGE}
          </p>
        )}
        {failure && (
          <div className={styles.failure} role='alert'>
            <span className={styles.failureTitle}>{failure.title}</span>
            {failure.message && <span>{failure.message}</span>}
          </div>
        )}
        {waiting.isRegistered && (
          <div className={styles.actions}>
            <button
              type='button'
              className={`${styles.button} ${styles.secondary}`}
              onClick={() => setShowTournamentDetails(true)}>
              Tournament details
            </button>
            {/* After a refusal, while no answer comes, or when the request
                could not be sent, the player may still be registered, and this
                panel is their only way off the table (Exit Table is hidden for
                a registrant): the exit the pre-start modal used to take. It
                sends no unregister. */}
            {(failure || stillConfirming || connectionLost) && (
              <button
                type='button'
                className={`${styles.button} ${styles.secondary}`}
                onClick={leaveTable}>
                Leave table
              </button>
            )}
            <button
              type='button'
              className={`${styles.button} ${styles.danger}`}
              onClick={requestUnregister}
              disabled={pending}
              aria-busy={pending}>
              {pending ? 'Unregistering…' : 'Unregister'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

/**
 * EN-11257 (release.sit-go-seated-waiting): the felt's panel while a Sit & Go
 * fills — how many players it still needs, that it starts when full, the
 * buy-in and the prize — for every viewer. A registered viewer also gets
 * Unregister and Tournament details: this panel replaces the pre-start modal,
 * which was their only way off the table (Exit Table is hidden for a
 * registered player), so the two are retired and introduced under one flag.
 *
 * Rendered inside the table root beside the seats, so it shares their stacking
 * context and sits under them (TableZ.sitGoWaitingPanel). Mounted for every
 * Sit & Go table so the flag's exposure is recorded at the waiting table on
 * both paths; with the flag off it renders nothing.
 */
const SitGoWaitingPanel = () => {
  const enabled = useFeatureFlag('sitGoSeatedWaiting');
  const waiting = useSitGoPreStart();
  useFeatureFlagExposure('sitGoSeatedWaiting', 'sit_go_waiting', waiting !== null);

  if (!enabled || !waiting) return null;
  return <WaitingPanel waiting={waiting} />;
};

export default SitGoWaitingPanel;
